import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from 'antd';
import { IProfessorAreaAssessmentShow } from 'interfaces/professor_area/assessments';
import { TabAttempts } from './TabAttempts/TabAttempts';
import { TabGeneral } from './TabGeneral/TabGeneral';
import { TabQuestions } from './TabQuestions/TabQuestions';
import { useCan } from '@refinedev/core';
import { useCustomSearchParams } from 'hooks/useSearchParams';

export const EvaluationsTabList = ({
  evaluation
}: {
  evaluation: IProfessorAreaAssessmentShow;
}) => {
  const { currentSearchParams: currentTab, setSearchParams } =
    useCustomSearchParams({
      searchParams: 'tab'
    });

  const [activeTabKey, setActiveTabKey] = useState<string>(
    currentTab || 'general'
  );
  const { data: attemptsPermission } = useCan({
    resource: 'professor_area/classroom_assessments/attempts',
    action: 'list'
  });
  const isAttemptsPermissionResolved = attemptsPermission !== undefined;
  const canListAttempts = Boolean(attemptsPermission?.can);

  const tabContent: Record<string, React.ReactNode> | null = useMemo(() => {
    if (!evaluation) {
      return null;
    }

    return {
      general: <TabGeneral evaluation={evaluation} />,
      questions: <TabQuestions evaluation={evaluation} />,
      attempts: <TabAttempts evaluation={evaluation} />
    };
  }, [evaluation]);

  useEffect(() => {
    if (currentTab) {
      setActiveTabKey(currentTab);
    }
  }, [currentTab]);

  useEffect(() => {
    if (
      isAttemptsPermissionResolved &&
      !canListAttempts &&
      activeTabKey === 'attempts'
    ) {
      setActiveTabKey('general');
      setSearchParams('general');
    }
  }, [
    activeTabKey,
    canListAttempts,
    isAttemptsPermissionResolved,
    setSearchParams
  ]);

  const onTabChange = useCallback(
    (tab: string) => {
      setActiveTabKey(tab);
      setSearchParams(tab);
    },
    [setSearchParams]
  );

  return (
    <Card
      tabList={[
        {
          key: 'general',
          label: 'Visão geral'
        },
        {
          key: 'questions',
          label: 'Questões'
        },
        ...(canListAttempts
          ? [
              {
                key: 'attempts',
                label: 'Avaliações realizadas'
              }
            ]
          : [])
      ]}
      activeTabKey={activeTabKey}
      onTabChange={onTabChange}
      prefixCls="custom-card"
    >
      {tabContent![activeTabKey]}
    </Card>
  );
};
